import React, { useState, useRef, useEffect } from 'react';
import { Property } from '../../types';
import { getPropertyCoverImageUrl, PROPERTY_IMAGE_PLACEHOLDER } from '../../services/api';
import { Search, ChevronDown, X } from 'lucide-react';

interface PropertySelectProps {
  properties: Property[];
  value: string;
  onChange: (propertyId: string) => void;
  error?: string;
  placeholder?: string;
  showPropertyCost?: boolean;
}

export const PropertySelect: React.FC<PropertySelectProps> = ({
  properties,
  value,
  onChange,
  error,
  placeholder = 'Seleccione una propiedad',
  showPropertyCost = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedProperty = properties.find(p => p.id === value);

  const formatProjectCost = (value: number) =>
    Number(value).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const filteredProperties = properties.filter(property =>
    property.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    property.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
    property.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (propertyId: string) => {
    onChange(propertyId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  return (
    <div className="relative property-select-light" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary flex items-center justify-between bg-white text-gray-900 border-gray-300 hover:bg-gray-50 ${
          error ? 'border-red-500' : 'border-gray-300'
        }`}
      >
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          {selectedProperty ? (
            <>
              {selectedProperty.coverImage && (
                <img
                  src={getPropertyCoverImageUrl(selectedProperty.coverImage)}
                  alt={selectedProperty.name}
                  className="w-10 h-10 rounded object-cover flex-shrink-0"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = PROPERTY_IMAGE_PLACEHOLDER;
                  }}
                />
              )}
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {selectedProperty.name}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {showPropertyCost
                    ? `${selectedProperty.city} - $${formatProjectCost(selectedProperty.totalValue)}`
                    : selectedProperty.city}
                </p>
              </div>
            </>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
          {selectedProperty && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          )}
          <ChevronDown
            className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
          />
        </div>
      </button>

      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}

      {isOpen && (
        <div className="absolute z-[100] w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-96 overflow-hidden text-gray-900">
          {/* Buscador - estilo pill con borde */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar propiedad..."
                className="w-full pl-10 pr-4 py-2.5 bg-white text-gray-900 border-2 border-primary/30 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm placeholder-gray-400"
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>

          {/* Lista de propiedades con separadores */}
          <div className="overflow-y-auto max-h-80">
            {filteredProperties.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                No se encontraron propiedades
              </div>
            ) : (
              filteredProperties.map((property) => (
                <button
                  key={property.id}
                  type="button"
                  onClick={() => handleSelect(property.id)}
                  className={`w-full px-4 py-3 flex items-center space-x-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                    value === property.id ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                    {property.coverImage ? (
                      <img
                        src={getPropertyCoverImageUrl(property.coverImage)}
                        alt={property.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = PROPERTY_IMAGE_PLACEHOLDER;
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-xs text-gray-500">Sin imagen</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {property.name}
                    </p>
                    <p className="text-xs text-gray-600 truncate mt-0.5">
                      {[property.address, property.city].filter(Boolean).join(', ')}
                    </p>
                    {showPropertyCost && (
                      <p className="text-sm font-semibold text-primary mt-1">
                        ${formatProjectCost(property.totalValue)}
                      </p>
                    )}
                  </div>
                  {value === property.id && (
                    <div className="flex-shrink-0">
                      <div className="w-2 h-2 bg-accent rounded-full" />
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

